import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Pagination,
  Autocomplete,
  Grid,
  TextField,
} from "@mui/material";
import api from "../../globals/axios";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import CustomAppBar from "../../components/AppBar";
import GenericButton from "../../components/Custom/GenericButton";
import { formatToIndianTime } from "../../globals/const";
import { useAlert } from "../../components/Alert";

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    minHeight: "100vh",
    marginBottom: "-4rem",
  },
  container: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  title: {
    textAlign: "center",
    padding: "20px 0",
    borderRadius: "4px",
    marginBottom: "20px",
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    padding: "20px 0",
  },
  filterContainer: {
    display: "flex",
    gap: "16px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
}));

const BiocharDetails = () => {
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const classes = useStyles();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const { showAlert } = useAlert();
  const [filters, setFilters] = useState({
    state: "",
    district: "",
    pincode: "",
    fpo_name: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    total_pages: 0,
    total_objects: 0,
  });

  const fetchBiocharData = (page = 1) => {
    setIsLoading(true);
    let { state, district, pincode, fpo_name } = filters;
    let query = `?page=${page}`;

    if (state) query += `&state=${state.id}`;
    if (district) query += `&district=${district.id}`;
    if (pincode) query += `&pincode=${pincode}`;
    if (fpo_name) query += `&fpo_name=${fpo_name}`;

    let url = `app-admin/biochar-interests/${query}`;

    api
      .get(url)
      .then((res) => {
        if (res?.data) {
          const flattenedResults = res.data.results.map((item) => {
            const flattenedItem = {
              ...item,
              ...item.address_data,
            };
            delete flattenedItem.address_data; // Removes the nested address_data object
            return flattenedItem;
          });
          setData(flattenedResults);
          setPagination({
            page: res.data.current_page_number,
            total_pages: res.data.total_pages,
            total_objects: res.data.total_objects,
          });
        }
        setError("");
      })
      .catch((err) => {
        console.error("Error fetching biochar interests:", err);
        setError(t("Couldn't fetch the biochar data. Please try again later!"));
        showAlert(
          "error",
          t("Couldn't fetch the biochar data. Please try again later!")
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchStates = async () => {
    try {
      const response = await api.get(`/common/states`);
      const data = response.data;
      setStates(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBiocharData();
    fetchStates();
  }, []);

  const handleViewDetails = (biocharId) => {
    navigate(`/biochar-details/${biocharId}`);
  };

  // Define table columns
  const columns = [
    { key: "fpo_name", label: t("FPO Name") },
    { key: "contact_person", label: t("Contact Person") },
    { key: "mobile_no", label: t("Mobile Number") },
    { key: "total_acreage", label: t("Total Acerage") },
    { key: "created_at", label: t("Created At") },
    { key: "state", label: t("State") },
    { key: "district", label: t("District") },
    { key: "pin_code", label: t("Pincode") },
    { key: "status", label: t("Status") },
  ];

  const handlePageChange = (event, value) => {
    fetchBiocharData(value);
  };

  const handleStateChange = async (e, newValue) => {
    setFilters((prev) => ({
      ...prev,
      state: newValue || "",
    }));
    try {
      const res = await api.get(`/common/districts?state_id=${newValue.id}`);
      setDistricts(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDistrictChange = (e, newValue) => {
    setFilters((prev) => ({
      ...prev,
      district: newValue || "",
    }));
  };

  return (
    <Box className={classes.root} component={Paper}>
      <CustomAppBar />

      <Box className={classes.container}>
        <Paper className={classes.title} elevation={0}>
          <Typography variant="h5">{t("Biochar Plant Interests")}</Typography>
        </Paper>
        <Box className={classes.filterContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                disablePortal
                id="state-select"
                options={states}
                getOptionLabel={(option) => option.name}
                value={filters.state || null}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                label={t("State")}
                renderInput={(params) => (
                  <TextField {...params} label={t("Select State")} />
                )}
                onChange={handleStateChange}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                disablePortal
                id="district-select"
                options={districts}
                getOptionLabel={(option) => option.name}
                value={filters.district || null}
                label={t("District")}
                renderInput={(params) => (
                  <TextField {...params} label={t("Select District")} />
                )}
                onChange={handleDistrictChange}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label={t("Pincode")}
                value={filters.pincode}
                onChange={(e) =>
                  setFilters({ ...filters, pincode: e.target.value })
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label={t("FPO Name")}
                value={filters.fpo_name}
                onChange={(e) =>
                  setFilters({ ...filters, fpo_name: e.target.value })
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <GenericButton
                backgroundColor="#4CAF50"
                fontColor="#fff"
                fontSize="14px"
                fullWidth
                padding="15px 10px"
                onClick={() => fetchBiocharData()}
              >
                {t("Search")}
              </GenericButton>
            </Grid>
          </Grid>
        </Box>
        <TableContainer component={Paper}>
          <Table sx={{ borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align="center"
                    sx={{
                      borderRight: "1px solid rgba(224, 224, 224, 1)",
                      fontWeight: "bold",
                      backgroundColor: "#FFC107",
                      color: "white",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#FFC107",
                    color: "white",
                    borderRight: "1px solid rgba(224, 224, 224, 1)",
                  }}
                >
                  {t("Action")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    {t("Loading")}...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    {error}
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    {t("No Records Found")}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((details, index) => (
                  <TableRow key={index}>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align="center"
                        sx={{
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {column.key == "created_at"
                          ? formatToIndianTime(details[column.key])
                          : details[column.key] || "-"}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <GenericButton
                        backgroundColor="#4CAF50"
                        fontColor="#fff"
                        fontSize="12px"
                        padding="5px 10px"
                        onClick={() => handleViewDetails(details.id)}
                      >
                        {t("View")}
                      </GenericButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {data.length > 0 && (
          <Box className={classes.pagination}>
            <Pagination
              count={pagination.total_pages}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
              disabled={isLoading}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BiocharDetails;
